import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/data/translations";
import { Phone, Menu, X, MessageCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";

const navLinks = [
  { key: "home", to: "/" },
  { key: "services", to: "/services/business-car" },
  { key: "routes", to: "/routes" },
  { key: "about", to: "/about" },
  { key: "contacts", to: "/contacts" },
  { key: "faq", to: "/faq" },
];

export default function Layout({ children }) {
  const { lang, toggleLang } = useLang();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const nav = t.nav[lang];
  const footer = t.footer[lang];

  return (
    <div className="min-h-screen flex flex-col bg-[#FDFDFD]">
      {/* Header */}
      <header
        data-testid="site-header"
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#E5E5E5]"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 h-14 sm:h-16">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="font-heading text-lg sm:text-xl font-bold tracking-tight text-[#0A0B10] uppercase flex-shrink-0">
            Transloggroup
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-8" data-testid="desktop-nav">
            {navLinks.map((l) => (
              <Link
                key={l.key}
                to={l.to}
                data-testid={`nav-${l.key}`}
                className={`text-sm font-medium tracking-wide transition-colors hover:text-[#FF3B30] whitespace-nowrap ${
                  location.pathname === l.to ? "text-[#FF3B30]" : "text-[#0A0B10]"
                }`}
              >
                {nav[l.key]}
              </Link>
            ))}
          </nav>

          {/* Right side: lang + CTA */}
          <div className="hidden lg:flex items-center gap-3 xl:gap-4 flex-shrink-0">
            <button
              data-testid="lang-switcher"
              onClick={toggleLang}
              className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0A0B10] border border-[#0A0B10] px-3 py-1.5 hover:bg-[#F5F5F5] transition-colors"
            >
              {lang === "cs" ? "EN" : "CZ"}
            </button>
            <a href="tel:+420607179165" data-testid="header-phone-cta">
              <Button className="rounded-none bg-[#FF3B30] text-white hover:bg-[#0A0B10] text-sm font-medium px-4 xl:px-5 h-9">
                <Phone className="w-4 h-4 mr-1" />
                <span className="hidden xl:inline">+420 607 179 165</span>
                <span className="xl:hidden">{lang === "cs" ? "Volat" : "Call"}</span>
              </Button>
            </a>
          </div>

          {/* Mobile/Tablet burger */}
          <div className="flex lg:hidden items-center gap-3">
            <button
              data-testid="mobile-lang-switcher-header"
              onClick={toggleLang}
              className="text-xs uppercase tracking-[0.15em] font-semibold text-[#0A0B10] border border-[#0A0B10] px-2.5 py-1 hover:bg-[#F5F5F5] transition-colors"
            >
              {lang === "cs" ? "EN" : "CZ"}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              className="text-[#0A0B10] p-1"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile/Tablet Nav */}
        {mobileOpen && (
          <div data-testid="mobile-nav" className="lg:hidden bg-white border-t border-[#E5E5E5] px-4 sm:px-6 py-5 space-y-3 max-h-[calc(100vh-3.5rem)] overflow-y-auto">
            {navLinks.map((l) => (
              <Link
                key={l.key}
                to={l.to}
                data-testid={`mobile-nav-${l.key}`}
                onClick={() => setMobileOpen(false)}
                className={`block text-base font-medium py-1 ${
                  location.pathname === l.to ? "text-[#FF3B30]" : "text-[#0A0B10]"
                }`}
              >
                {nav[l.key]}
              </Link>
            ))}
            <div className="flex items-center gap-3 pt-4 border-t border-[#E5E5E5]">
              <a href="tel:+420607179165" data-testid="mobile-phone-cta" className="flex-1">
                <Button className="rounded-none bg-[#FF3B30] text-white hover:bg-[#0A0B10] text-sm px-4 h-10 w-full">
                  <Phone className="w-4 h-4 mr-2" />
                  +420 607 179 165
                </Button>
              </a>
              <a href="https://wa.me/420607179165" target="_blank" rel="noreferrer" data-testid="mobile-whatsapp-cta">
                <Button variant="outline" className="rounded-none border-[#0A0B10] text-[#0A0B10] text-sm px-4 h-10">
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1 pt-14 sm:pt-16">{children}</main>

      {/* Footer */}
      <footer data-testid="site-footer" className="bg-[#0A0B10] text-white py-12 sm:py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-10 md:gap-12">
            <div>
              <h3 className="font-heading text-2xl font-bold tracking-tight mb-4">Transloggroup</h3>
              <p className="text-white/60 text-sm leading-relaxed max-w-xs">
                {lang === "cs"
                  ? "Rodinná přepravní společnost z České republiky. Osobní i nákladní přeprava po celé Evropě."
                  : "Family transport company from the Czech Republic. Passenger and cargo transport across Europe."}
              </p>
              <div className="mt-4 space-y-1 text-white/40 text-xs leading-relaxed">
                <p>IS-transloggroup s.r.o.</p>
                <p>Oblouková 848/25, Vršovice, 101 00 Praha 10</p>
                <p>{lang === "cs" ? "IČO" : "ID"}: 249 21 025</p>
                <p>{lang === "cs" ? "Společnost s ručením omezeným" : "Limited liability company"}</p>
              </div>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-[0.2em] font-semibold mb-6 text-white/40">
                {lang === "cs" ? "Navigace" : "Navigation"}
              </h4>
              <div className="space-y-3">
                {navLinks.map((l) => (
                  <Link
                    key={l.key}
                    to={l.to}
                    data-testid={`footer-nav-${l.key}`}
                    className="block text-sm text-white/70 hover:text-[#FF3B30] transition-colors"
                  >
                    {nav[l.key]}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-[0.2em] font-semibold mb-6 text-white/40">
                {lang === "cs" ? "Kontakt" : "Contact"}
              </h4>
              <div className="flex flex-col gap-4">
                <a href="tel:+420607179165" data-testid="footer-phone" className="text-white/70 hover:text-white transition-colors" title={lang === "cs" ? "Telefon" : "Phone"}>
                  <Phone className="w-10 h-10" style={{ color: "#25D366" }} />
                </a>
                <a href="https://wa.me/420607179165" data-testid="footer-whatsapp" target="_blank" rel="noreferrer" className="hover:opacity-80 transition-opacity" title="WhatsApp">
                  <svg className="w-10 h-10" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </a>
                <a href="https://t.me/+420607179165" data-testid="footer-telegram" target="_blank" rel="noreferrer" className="hover:opacity-80 transition-opacity" title="Telegram">
                  <svg className="w-10 h-10" viewBox="0 0 24 24" fill="#26A5E4"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                </a>
                <a href="https://www.instagram.com/transloggroup" data-testid="footer-instagram" target="_blank" rel="noreferrer" className="hover:opacity-80 transition-opacity" title="Instagram">
                  <svg className="w-10 h-10" viewBox="0 0 24 24"><defs><radialGradient id="ig" r="150%" cx="30%" cy="107%"><stop offset="0" stopColor="#fdf497"/><stop offset=".05" stopColor="#fdf497"/><stop offset=".45" stopColor="#fd5949"/><stop offset=".6" stopColor="#d6249f"/><stop offset=".9" stopColor="#285AEB"/></radialGradient></defs><rect x="2" y="2" width="20" height="20" rx="5" fill="url(#ig)"/><circle cx="12" cy="12" r="3.8" fill="none" stroke="#fff" strokeWidth="1.6"/><circle cx="17.2" cy="6.8" r="1" fill="#fff"/></svg>
                </a>
              </div>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-white/40">
            <span>© {new Date().getFullYear()} {footer.company} — {footer.rights}</span>
            <span>
              Developed by{" "}
              <a href="https://victoryinteractive.dev" target="_blank" rel="noreferrer" data-testid="footer-developer-link" className="text-white/60 hover:text-[#FF3B30] transition-colors">
                Victory Interactive Inc
              </a>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
